import {languageKey} from '@/service/keywordStemDensity';
import {stripTagsAndEntities} from '@/utils/string';

const EN_PROMO_RE = /\b(sign up|subscribe|newsletter|cookie policy|all rights reserved|click here|learn more|read more|get started|try free|download app)\b/i;

const EN_STOP = new Set([
    'the', 'and', 'for', 'with', 'your', 'you', 'our', 'this', 'that', 'from', 'are', 'was', 'have', 'has',
    'will', 'can', 'should', 'what', 'when', 'where', 'which', 'how', 'why', 'about', 'into', 'their',
]);

const CS_STOP = new Set([
    'a', 'i', 'v', 'na', 'do', 'od', 'pro', 'je', 'jsou', 'že', 'to', 'ta', 'ten', 'toto', 'který', 'která',
    'jak', 'co', 'kdy', 'kde', 'nebo', 'ale', 'už', 'jen', 'aby', 'pokud',
]);

const CS_DIACRITIC_RE = /[áčďéěíňóřšťúůýžÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]/;

function tokenize(value: string): string[] {
    return String(value || '')
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter((word) => word.length > 1);
}

/** True when phrase is appropriate for the article language (drops obvious wrong-language scrap junk). */
export function textMatchesArticleLanguage(text: unknown, languageCode?: string | null): boolean {
    const raw = stripTagsAndEntities(String(text || ''));
    if (!raw.trim()) {
        return false;
    }
    if (EN_PROMO_RE.test(raw)) {
        return languageKey(languageCode) === 'en';
    }
    const lang = languageKey(languageCode);
    const tokens = tokenize(raw);
    if (!tokens.length) {
        return true;
    }
    if (lang === 'en') {
        const csHits = tokens.filter((t) => CS_STOP.has(t)).length;
        const enHits = tokens.filter((t) => EN_STOP.has(t)).length;
        if (csHits >= 2 && enHits === 0 && CS_DIACRITIC_RE.test(raw)) {
            return false;
        }
        return true;
    }
    // Czech (and sk) articles: drop clearly English-only promo / FAQ lines.
    const enHits = tokens.filter((t) => EN_STOP.has(t)).length;
    const csHits = tokens.filter((t) => CS_STOP.has(t)).length;
    const hasCzechDiacritics = CS_DIACRITIC_RE.test(raw);
    if (enHits >= 2 && csHits === 0 && !hasCzechDiacritics) {
        return false;
    }
    if (tokens.length >= 4 && enHits >= Math.max(2, Math.floor(tokens.length * 0.35)) && !hasCzechDiacritics) {
        return false;
    }
    return true;
}

export function filterPhrasesForArticleLanguage<T extends string>(
    phrases: T[],
    languageCode?: string | null
): T[] {
    return phrases.filter((phrase) => textMatchesArticleLanguage(phrase, languageCode));
}
